import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { esRpcInexistente } from "@/lib/rpcErrors";
import { idsBancosRechazados, rechazosPorBanco, type BancoDisponibleRow } from "@/lib/portal-bancos/bancos-disponibles";

/**
 * Bancos que ya rechazaron la solicitud de crédito de la propiedad de la
 * cuenta (`bancos_disponibles_para_cuenta`). Si la RPC todavía no existe en el
 * ambiente, no filtra nada: el guard de BD tampoco existe ahí.
 */
async function fetchFilas(cuentaId: number): Promise<BancoDisponibleRow[]> {
  const { data, error } = await (supabase as any).rpc("bancos_disponibles_para_cuenta", {
    p_id_cuenta_cobranza: cuentaId,
  });
  if (error) {
    if (esRpcInexistente(error)) return [];
    throw error;
  }
  return (data as BancoDisponibleRow[] | null) ?? [];
}

export async function fetchBancosRechazados(cuentaId: number): Promise<Set<number>> {
  return idsBancosRechazados(await fetchFilas(cuentaId));
}

export const bancosRechazadosKey = (cuentaId?: number | null) =>
  ["bancos-rechazados", cuentaId ?? "none"] as const;

export function useBancosRechazados(cuentaId?: number | null) {
  return useQuery({
    queryKey: bancosRechazadosKey(cuentaId),
    enabled: cuentaId != null && cuentaId > 0,
    staleTime: 30_000,
    retry: false,
    queryFn: () => fetchBancosRechazados(cuentaId as number),
  });
}

/** Bancos que rechazaron la propiedad con la fecha del rechazo (para cobranza). */
export function useRechazosBancos(cuentaId?: number | null) {
  return useQuery({
    queryKey: ["bancos-rechazos-fecha", cuentaId ?? "none"] as const,
    enabled: cuentaId != null && cuentaId > 0,
    staleTime: 30_000,
    retry: false,
    queryFn: async () => rechazosPorBanco(await fetchFilas(cuentaId as number)),
  });
}
